import "./styles.scss"

import React, { useEffect, useState, useRef } from "react"
import { Link } from "gatsby"
import clsx from "clsx"

import { useLocale } from "context/locale"
import { useWindow } from "context/window"
import useResponsive from "hooks/useResponsive"

import Button from "components/Button"
import { ContactSubmenu, CompanySubmenu, ServicesSubmenu } from "./components"

import { DASHBOARD_URL } from "utils"

import IcoArrow from "assets/icons/header-arrow.svg"

const Header = ({ services, isHome }) => {
  const { t, lang } = useLocale()
  const { isBrowser } = useWindow()
  const { isDesktop } = useResponsive()

  const navInnerRef = useRef(null)

  const [submenu, setSubmenu] = useState("")
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const isServicesSubmenu = services
    ?.map(service => service.name)
    .some(service => service === submenu)

  const handleMenuClose = () =>
    isDesktop ? setSubmenu("") : setIsMenuOpen(false)

  const resetNavInnerScrollPosition = () => {
    if (navInnerRef.current) {
      navInnerRef.current.scrollTop = 0
    }
  }

  useEffect(() => {
    window.addEventListener("scroll", () => {
      setIsScrolled(window.scrollY > 1)
    })
  })

  useEffect(() => {
    const className = "header-menu--open"

    if (isMenuOpen) {
      document.body.classList.add(className)
    } else {
      document.body.classList.remove(className)
    }
  }, [isMenuOpen])

  useEffect(() => {
    if (isDesktop && isMenuOpen) {
      setSubmenu("")
      setIsMenuOpen(false)
    }
  }, [isDesktop, isMenuOpen])

  return (
    <header
      className={clsx(
        "header",
        isScrolled && "header--shrink",
        !isHome && "header--subpage"
      )}
      role="presentation"
      onMouseLeave={isBrowser && isDesktop ? () => setSubmenu("") : undefined}
    >
      <div className="header__contact">
        <div className="header__contact-block">
          <p>
            {t("Have questions?")} {t("Contact us:")}
          </p>
          <ul>
            <li>
              <a href="tel:+48690888692">+48 690 888 692</a>
            </li>
            <li>
              <a href="mailto:contact@webinity.com">contact@webinity.com</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="header__main">
        <div className="header__brand">
          <Link to={t("/")} className="header__logo" onClick={handleMenuClose}>
            <img
              className="desktop"
              src={require("assets/images/logo-animated.gif").default}
              alt=""
            />
            <img
              className="mobile"
              src={require("assets/images/logo.svg").default}
              alt=""
            />
          </Link>
        </div>
        <nav className={clsx("header__nav", isMenuOpen && "nav-open")}>
          <div className="header__nav-outer">
            <div className="header__nav-inner" ref={navInnerRef}>
              {isBrowser && !isDesktop && submenu !== "" && (
                <button
                  className="header__nav-back"
                  onClick={() => {
                    resetNavInnerScrollPosition()
                    setSubmenu(isServicesSubmenu ? "services" : "")
                  }}
                >
                  <span className="header__nav-arrow">
                    <img src={IcoArrow} alt="" />
                  </span>
                  {t("Back")} {isServicesSubmenu ? "to Services" : ""}
                </button>
              )}
              {isBrowser && (isDesktop || (!isDesktop && submenu === "")) && (
                <>
                  <div className="header__nav-item">
                    <Link
                      to={t("/pricing/")}
                      className="header__nav-link"
                      onClick={handleMenuClose}
                    >
                      {t("Pricing")}
                    </Link>
                  </div>
                  {services && (
                    <div
                      className={clsx(
                        "header__nav-item",
                        isBrowser &&
                          isDesktop &&
                          submenu === "services" &&
                          "submenu-open"
                      )}
                    >
                      <button
                        className="header__nav-link"
                        onClick={
                          isBrowser && !isDesktop
                            ? () => {
                                resetNavInnerScrollPosition()
                                setSubmenu("services")
                              }
                            : undefined
                        }
                        onMouseEnter={
                          isBrowser && isDesktop
                            ? () => setSubmenu("services")
                            : undefined
                        }
                      >
                        {t("Services")}{" "}
                        <span className="header__nav-arrow">
                          <img src={IcoArrow} alt="" />
                        </span>
                      </button>
                      {isBrowser && isDesktop && (
                        <ServicesSubmenu
                          services={services}
                          isServicesSubmenu={isServicesSubmenu}
                          onClick={handleMenuClose}
                        />
                      )}
                    </div>
                  )}
                  <div className="header__nav-item">
                    <Link
                      to={t("/faq/")}
                      className="header__nav-link"
                      onClick={handleMenuClose}
                    >
                      {t("FAQ")}
                    </Link>
                  </div>
                  <div className="header__nav-item">
                    <Link
                      to={t("/testimonials/")}
                      className="header__nav-link"
                      onClick={handleMenuClose}
                    >
                      {t("Testimonials")}
                    </Link>
                  </div>
                  <div
                    className={clsx(
                      "header__nav-item",
                      isBrowser &&
                        isDesktop &&
                        submenu === "company" &&
                        "submenu-open"
                    )}
                  >
                    <button
                      className="header__nav-link"
                      onClick={
                        isBrowser && !isDesktop
                          ? () => {
                              resetNavInnerScrollPosition()
                              setSubmenu("company")
                            }
                          : undefined
                      }
                      onMouseEnter={
                        isBrowser && isDesktop
                          ? () => setSubmenu("company")
                          : undefined
                      }
                    >
                      {t("Company")}{" "}
                      <span className="header__nav-arrow">
                        <img src={IcoArrow} alt="" />
                      </span>
                      {isBrowser && isDesktop && (
                        <CompanySubmenu onClick={handleMenuClose} />
                      )}
                    </button>
                  </div>
                  <div className="header__nav-item">
                    <Link
                      to={t("/blog/")}
                      className="header__nav-link"
                      onClick={handleMenuClose}
                    >
                      {t("Blog")}
                    </Link>
                  </div>
                  <div
                    className={clsx(
                      "header__nav-item",
                      isBrowser &&
                        isDesktop &&
                        submenu === "contact" &&
                        "submenu-open"
                    )}
                  >
                    {isBrowser && isDesktop ? (
                      <>
                        <Link
                          to={t("/contact/")}
                          className="header__nav-link"
                          onMouseEnter={() => setSubmenu("contact")}
                        >
                          {t("Contact")}{" "}
                          <span className="header__nav-arrow">
                            <img src={IcoArrow} alt="" />
                          </span>
                        </Link>
                        <ContactSubmenu onClick={handleMenuClose} />
                      </>
                    ) : (
                      <button
                        className="header__nav-link"
                        onClick={() => {
                          resetNavInnerScrollPosition()
                          setSubmenu("contact")
                        }}
                      >
                        {t("Contact")}{" "}
                        <span className="header__nav-arrow">
                          <img src={IcoArrow} alt="" />
                        </span>
                      </button>
                    )}
                  </div>
                </>
              )}

              {isBrowser && !isDesktop && (
                <>
                  {(submenu === "services" || isServicesSubmenu) && (
                    <ServicesSubmenu
                      services={services}
                      submenu={submenu}
                      setSubmenu={setSubmenu}
                      isServicesSubmenu={isServicesSubmenu}
                      onClick={handleMenuClose}
                    />
                  )}
                  {submenu === "company" && (
                    <CompanySubmenu onClick={handleMenuClose} />
                  )}
                  {submenu === "contact" && (
                    <ContactSubmenu onClick={handleMenuClose} />
                  )}
                </>
              )}
            </div>
            <div className="header__nav-footer">
              <p>{t("Log in to your account or create one")}</p>
              <div className="header__nav-cta">
                <Button
                  className="button--small button--transparent-turqoise"
                  to={`${DASHBOARD_URL}login?lang=${lang}`}
                  onClick={() =>
                    isBrowser &&
                    window?.gtag("event", "log_in", {
                      click_text: t("Log in"),
                      click_url: `${DASHBOARD_URL}login?lang=${lang}`,
                    })
                  }
                >
                  {t("Log in")}
                </Button>
                <Button
                  className="button--small"
                  to={`${DASHBOARD_URL}register?lang=${lang}`}
                >
                  {t("Sign up")}
                </Button>
              </div>
            </div>
          </div>
        </nav>
        <div className="header__cta">
          <Button
            className="button--small button--transparent-light"
            to={`${DASHBOARD_URL}login?lang=${lang}`}
          >
            {t("Log in")}
          </Button>
          <Button
            className="button--small button--text-dark"
            to={`${DASHBOARD_URL}register?lang=${lang}`}
          >
            {t("Sign up")}
          </Button>
        </div>
        <button
          aria-label="Open Menu"
          className={clsx("hamburger", isMenuOpen && "is-active")}
          onClick={() =>
            setIsMenuOpen(prevMenuOpen => {
              if (!prevMenuOpen) {
                resetNavInnerScrollPosition()

                setSubmenu("")
              }

              return !prevMenuOpen
            })
          }
        >
          {Array(3)
            .fill(null)
            .map((_, index) => (
              <span key={index} />
            ))}
        </button>
      </div>
    </header>
  )
}

export default Header
